import { useEffect } from 'react';
import PropTypes from 'prop-types';

import { captureAnalytics } from '../../actions/preload';
import { shouldAutoOpenTutorialMenu } from './tutorialProgress';

/**
 * Evaluates the post-walkthrough checklist auto-open, exactly once per user.
 *
 * A functional component with a single latch (redux `autoOpenEvaluated`). It was
 * a class observer carrying its OWN third latch (`evaluatedUserEmail`) on top of
 * the redux one and `useTutorialLifecycle`'s `resumeEvaluatedRef` — three
 * one-shots for one feature, and the only class component in it.
 *
 * An effect is the right shape after all: the thing being observed is a prop
 * transition (profile hydration, then walkthrough completion), which is precisely
 * what an effect dependency list expresses.
 */
const TutorialAutoOpen = ({
  autoOpenEvaluated,
  impersonating,
  userProfileLoaded,
  userEmail,
  hasCompletedWalkthrough,
  hasCompletedTutorial,
  walkthroughStartDestination,
  progress,
  tutorials,
  markAutoOpenEvaluated,
  openTutorialMenu,
}) => {
  useEffect(() => {
    if (autoOpenEvaluated || impersonating || !userEmail) return;

    // `userProfileLoaded` must mean "the profile FETCH has landed", not "an email
    // exists somewhere". It used to be satisfied by `m8_uid`, which every login
    // path writes at sign-in — so this ran with `hasCompletedTutorial` still
    // false, burned the latch, and popped the checklist open for users the server
    // already had marked done. The flag only reaches redux via
    // FINISHED_GET_USER_INFO, so that is what has to gate this.
    if (!userProfileLoaded) return;

    // Do NOT consume the one-shot latch before the user is even eligible.
    //
    // LearnMedsender — and therefore this observer — is mounted app-wide by
    // containers/App, including on `/app/walkthrough`, and the walkthrough
    // finishes with an in-app `history.push` (containers/walkthrough/
    // IntroToMedsender), so redux is never torn down. Marking the latch as soon as
    // the profile hydrated therefore burned it while `hasCompletedWalkthrough` was
    // still false, and the later false→true transition hit the guard above: the
    // checklist never opened for exactly the users it exists for.
    //
    // `shouldAutoOpenTutorialMenu` checks this too, but the check has to happen
    // BEFORE the latch is written, not after.
    if (!hasCompletedWalkthrough) return;

    markAutoOpenEvaluated();

    if (shouldAutoOpenTutorialMenu({
      hasCompletedWalkthrough,
      hasCompletedTutorial,
      walkthroughStartDestination,
      progress,
      tutorials,
    })) {
      captureAnalytics('tutorial_menu_opened', { source: 'walkthrough_completed' });
      openTutorialMenu();
    }
  }, [
    autoOpenEvaluated,
    impersonating,
    userProfileLoaded,
    userEmail,
    hasCompletedWalkthrough,
    hasCompletedTutorial,
    walkthroughStartDestination,
    progress,
    tutorials,
    markAutoOpenEvaluated,
    openTutorialMenu,
  ]);

  return null;
};

TutorialAutoOpen.defaultProps = {
  autoOpenEvaluated: false,
  impersonating: false,
  userProfileLoaded: false,
  userEmail: '',
  hasCompletedWalkthrough: false,
  hasCompletedTutorial: false,
  walkthroughStartDestination: null,
  progress: null,
  tutorials: [],
};

TutorialAutoOpen.propTypes = {
  autoOpenEvaluated: PropTypes.bool,
  impersonating: PropTypes.bool,
  userProfileLoaded: PropTypes.bool,
  userEmail: PropTypes.string,
  hasCompletedWalkthrough: PropTypes.bool,
  hasCompletedTutorial: PropTypes.bool,
  walkthroughStartDestination: PropTypes.string,
  progress: PropTypes.shape({
    completed: PropTypes.arrayOf(PropTypes.string),
    dismissed: PropTypes.bool,
  }),
  tutorials: PropTypes.arrayOf(PropTypes.object),
  markAutoOpenEvaluated: PropTypes.func.isRequired,
  openTutorialMenu: PropTypes.func.isRequired,
};

export default TutorialAutoOpen;
